import React, { useState } from "react";
import { Router, Route, match } from "react-router-dom";
import { createBrowserHistory, History } from "history";
import { ReactQueryConfigProvider } from "react-query";

import useInternalAxiosConfig from "fe-shared/src/hooks/useInternalAxiosConfig";
import queryConfig from "fe-shared/src/helpers/queryConfig";

import { User, UserInfo } from "fe-shared/src/types/domain";
import { NotificationsContainer } from "fe-shared/src/components";

import { INTERNAL_CLIENT_ID } from "fe-shared/src/consts";

import TransactionsGrid from "routes/Transactions";
import TransfersGrid from "routes/Transfers";
import TransactionDetails from "routes/TransactionDetails";
import TransferDetails from "routes/TransferDetails";
import InvoicesDetails from "routes/InvoicesDetails";
import InvoicesList from "routes/Invoices";
import InventoryAdjustmentsList from "routes/InventoryAdjustments";
import InventoryAdjustmentsDetails from "routes/InventoryAdjustmentsDetails";

import UserContext from "contexts/userContext";

import styles from "./App.module.scss";

enum Feature {
  Transfers = "Transfers",
  Transactions = "Transactions",
  Invoices = "Invoices",
  InventoryAdjustments = "InventoryAdjustments",
}

interface Props {
  history?: History;
  match?: match<{}>;
  feature?: Feature;
  apiBaseUrl?: string;
  user?: {
    data: User;
  };
  setUserData?: (data: UserInfo | null) => void;
  refreshTokenUrl?: string;
  forbiddenCallback?: () => void;
  sessionId?: string;
}

const defaultProps = {
  setUserData: (data: UserInfo | null) => data,
};

const App = (props: Props) => {
  const {
    apiBaseUrl,
    user,
    setUserData = defaultProps.setUserData,
    refreshTokenUrl,
    forbiddenCallback,
    feature,
    sessionId,
  } = props;

  const matchUrl = props.match?.url || "";
  const history = props.history || createBrowserHistory();
  const [
    lastOpenedTransactionNumber,
    setLastOpenedTransactionNumber,
  ] = useState<string | null>(null);
  const [lastOpenedTransfersNumber, setLastOpenedTransfersNumber] = useState<
    string | null
  >(null);
  const [lastOpenedInvoiceNumber, setLastOpenedInvoiceNumber] = useState<
    string | null
  >(null);
  const [
    lastOpenedInventoryAdjustmentNumber,
    setLastOpenedInventoryAdjustmentNumber,
  ] = useState<string | null>(null);
  const isReady = useInternalAxiosConfig(
    user,
    setUserData,
    apiBaseUrl,
    refreshTokenUrl,
    forbiddenCallback,
    sessionId,
    INTERNAL_CLIENT_ID
  );

  if (!isReady) {
    return null;
  }
  return (
    <ReactQueryConfigProvider config={queryConfig}>
      <UserContext.Provider value={user?.data}>
        <Router history={history}>
          <div className={styles.etTransactions}>
            {feature === Feature.Transactions && (
              <Route path={matchUrl} exact>
                <TransactionsGrid
                  lastOpenedNumber={lastOpenedTransactionNumber}
                  clearLastOpenedNumber={() => {
                    setLastOpenedTransactionNumber(null);
                  }}
                />
              </Route>
            )}
            {feature === Feature.Transactions && (
              <Route path={`${matchUrl}/:transactionNumber`}>
                <TransactionDetails
                  setLastOpenedNumber={setLastOpenedTransactionNumber}
                  apiBaseUrl={apiBaseUrl}
                />
              </Route>
            )}
            {feature === Feature.Transfers && (
              <Route path={matchUrl} exact>
                <TransfersGrid
                  lastOpenedTransfersNumber={lastOpenedTransfersNumber}
                  clearLastOpenedTransfersNumber={() => {
                    setLastOpenedTransfersNumber(null);
                  }}
                />
              </Route>
            )}
            {feature === Feature.Transfers && (
              <Route path={`${matchUrl}/:transferNumber`}>
                <TransferDetails
                  setLastOpenedTransfersNumber={setLastOpenedTransfersNumber}
                />
              </Route>
            )}
            {feature === Feature.Invoices && (
              <Route path={matchUrl} exact>
                <InvoicesList
                  lastOpenedInvoiceNumber={lastOpenedInvoiceNumber}
                  clearLastOpenedInvoiceNumber={() => {
                    setLastOpenedInvoiceNumber(null);
                  }}
                />
              </Route>
            )}
            {feature === Feature.Invoices && (
              <Route path={`${matchUrl}/:invoiceNumber`}>
                <InvoicesDetails
                  setLastOpenedInvoiceNumber={setLastOpenedInvoiceNumber}
                />
              </Route>
            )}
            {feature === Feature.InventoryAdjustments && (
              <Route path={matchUrl} exact>
                <InventoryAdjustmentsList
                  lastOpenedNumber={lastOpenedInventoryAdjustmentNumber}
                  clearLastOpenedNumber={() => {
                    setLastOpenedInventoryAdjustmentNumber(null);
                  }}
                />
              </Route>
            )}
            {feature === Feature.InventoryAdjustments && (
              <Route path={`${matchUrl}/:adjustmentNumber`}>
                <InventoryAdjustmentsDetails
                  setLastOpenedNumber={setLastOpenedInventoryAdjustmentNumber}
                />
              </Route>
            )}
          </div>
        </Router>
        <NotificationsContainer />
      </UserContext.Provider>
    </ReactQueryConfigProvider>
  );
};

export default App;
